export interface BucketSummaryItem {
  bucket: string;
  count: number;
}

// Nav item that spans every other Packet Buckets bucket — not a real bucket, so buckets-summary
// never returns it; the backend maps it to every bucket outside
// PacketBucketDefinitions.ExcludedFromAllReferrals and PacketBucketService.getBucketCounts sums
// those same buckets' counts for its own (see ALL_REFERRALS_COUNTED_BUCKETS).
export const ALL_REFERRALS_BUCKET = 'All referrals';

// Soft-deleted packets (current_status -1) — PacketBucketDefinitions.Deleted on the backend, which
// keys it on current_status rather than rq_status. Every other bucket, All referrals included,
// counts only live packets, so this one is left out of ALL_REFERRALS_COUNTED_BUCKETS.
export const DELETED_BUCKET = 'Deleted';

// Only bucket the sidebar nests a submenu under (see nav-content.component.ts) — "All Referrals"
// and "Priority Referrals" (Crisis + ASAP only), both still scoped to this same bucket.
export const PENDING_REFERRALS_BUCKET = 'Pending Referrals';

// Not a bucket of its own — the subset of Pending Referrals whose priority is Crisis/ASAP. Matches
// PacketBucketDefinitions.PriorityReferrals on the API, which buckets-summary returns as an extra
// row alongside the real buckets purely for this submenu badge (see getPacketCount in nav-content).
export const PRIORITY_REFERRALS_BUCKET = 'Priority Referrals';

// Client-side presentation for each bucket the Packet Buckets menu offers — the backend's
// PacketBucketDefinitions is the source of truth for which rq_statuses a bucket holds. Used by the
// sidebar submenu (nav-content), the header bell's Referral Queue panel (nav-right) and the
// Incoming Referrals breadcrumb (page-breadcrumb), so a bucket is only defined once.
//   order       — menu position. All referrals and Deleted come last: sidebar icon colors are
//                 positional (nth-child), so putting them first would recolor every other bucket.
//                 Deleted follows All referrals since it sits outside it.
//   icon        — ng-zorro icon, same in all three places.
//   tone        — the bell panel's icon/count color class (the sidebar colors by position instead,
//                 see --nav-icon-color in nav-content.component.scss).
//   description — the bell panel's one-line caption.
//   hidden      — kept out of the menus (see PACKET_BUCKET_ITEMS) while staying a real bucket: its
//                 rows are still in the All referrals grid and counted in its badge (see
//                 ALL_REFERRALS_COUNTED_BUCKETS), and ?bucket=<name> still resolves a breadcrumb
//                 title/icon here, so an existing link to it keeps working.
export const BUCKET_META: Record<string, { order: number; icon: string; tone: string; description: string; hidden?: boolean }> = {
  [PENDING_REFERRALS_BUCKET]: { order: 1, icon: 'clock-circle',  tone: 'amber',  description: 'Awaiting action' },
  'Duplicate Enrollment':   { order: 2, icon: 'copy',            tone: 'purple', description: 'Duplicate of an existing enrollment' },
  'Successfully Processed': { order: 3, icon: 'check-circle',    tone: 'green',  description: 'Processed successfully' },
  'System Error':           { order: 4, icon: 'close-circle',    tone: 'red',    description: 'Failed during processing' },
  'System Processing':      { order: 5, icon: 'sync',            tone: 'blue',   description: 'Being processed' },
  'Unmapped':               { order: 6, icon: 'question-circle', tone: 'grey',   description: 'Status not in any bucket', hidden: true },
  [ALL_REFERRALS_BUCKET]:   { order: 7, icon: 'file-text',       tone: 'blue',   description: 'All buckets combined' },
  [DELETED_BUCKET]:         { order: 8, icon: 'delete',          tone: 'grey',   description: 'Deleted packets' }
};

export interface PacketBucketItem {
  bucket: string;
  icon: string;
  tone: string;
  description: string;
}

// BUCKET_META as the menu renders it, in order — hidden buckets left out.
export const PACKET_BUCKET_ITEMS: PacketBucketItem[] = Object.entries(BUCKET_META)
  .filter(([, meta]) => !meta.hidden)
  .sort(([, a], [, b]) => a.order - b.order)
  .map(([bucket, { icon, tone, description }]) => ({ bucket, icon, tone, description }));

// The buckets All referrals spans, for summing its badge — every bucket including the hidden ones,
// because the backend scopes the All referrals grid by PacketBucketDefinitions.ExcludedFromAllReferrals
// (TBD only), not by what the menu shows. Summing the menu instead would drop a hidden bucket's
// packets from the badge while the grid still listed them. Deleted is the exception: the All
// referrals grid lists live packets only.
export const ALL_REFERRALS_COUNTED_BUCKETS: string[] = Object.keys(BUCKET_META).filter(
  (b) => b !== ALL_REFERRALS_BUCKET && b !== DELETED_BUCKET
);
